import React, { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import cn from 'classnames';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import './index.scss';

export default function CharmBraceletPagination({
  children,
  currentQuestionIndex,
  showDoubleArrow,
  goToQuestion,
  onPreviousClick,
  onNextClick,
  isPreview,
  showNavigationNumbers = true,
}) {
  const [perPage, setPerPage] = useState(0);
  const [index, setIndex] = useState({ from: 0, to: 0 });
  const [doubleArrowVisible, setShowDoubleArrow] = useState(false);
  const questionIconsElement = useRef(null);
  const sizerElement = useRef(null);

  const hasPreviousPage = index.from > 0;
  const hasNextPage = index.to < children.length;

  const measure = debounce(() => {
    if (questionIconsElement.current) {
      const questionPerPage = Math.floor(
        questionIconsElement.current.clientWidth /
          sizerElement.current.clientWidth,
      );

      const initPage = Math.ceil((currentQuestionIndex + 1) / questionPerPage);

      setPerPage(questionPerPage);
      setIndex({
        from: (initPage - 1) * questionPerPage,
        to: initPage * questionPerPage,
      });
    }
  }, 50);

  const childrenForCurrentPage = children.slice(index.from, index.to);

  useEffect(() => {
    // setup measures to correctly apply widths
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    measure();
  }, [
    questionIconsElement.current,
    questionIconsElement.current && questionIconsElement.current.clientWidth,
    showDoubleArrow,
  ]);

  const setNextStateIdx = () =>
    setIndex({
      from: index.from + 1,
      to: index.to + 1,
    });

  const setPrevIdxState = () =>
    setIndex({
      from: index.from - 1,
      to: index.to - 1,
    });

  const nextClicked = () => {
    onNextClick();
    if (currentQuestionIndex >= index.to - 1) {
      setNextStateIdx();
    }
  };

  const previousClicked = () => {
    onPreviousClick();
    if (currentQuestionIndex <= index.from) {
      setPrevIdxState();
    }
  };

  useEffect(() => {
    if (currentQuestionIndex > index.to - 1) {
      setNextStateIdx();
    }
    if (currentQuestionIndex < index.from) {
      setPrevIdxState();
    }
  }, [currentQuestionIndex]);

  function focusSelectedIcon() {
    setTimeout(() => {
      document
        .querySelector('.overview-icon-container.is-selected .overview-icon')
        .focus();
    }, 2000);
  }

  const moveToNextPage = () => {
    let nextFrom = index.from + perPage;
    let nextTo = index.to + perPage;

    if (nextTo >= children.length) {
      const delta = nextTo - children.length;
      nextTo -= delta;
      nextFrom -= delta;
    }

    setIndex({
      from: nextFrom,
      to: nextTo,
    });
    goToQuestion(nextFrom);
    focusSelectedIcon();
  };

  const moveToPreviousPage = () => {
    let prevFrom = index.from - perPage;
    let prevTo = index.to - perPage;

    if (prevFrom < 0) {
      const delta = Math.abs(prevFrom);
      prevFrom = 0;
      prevTo += delta;
    }

    setIndex({
      from: prevFrom,
      to: prevTo,
    });

    goToQuestion(prevFrom);
    focusSelectedIcon();
  };

  const moveToCurrentQuestion = () => {
    const currentPage = Math.floor(currentQuestionIndex / perPage);
    let nextFrom = currentPage * perPage;
    let nextTo = (currentPage + 1) * perPage;

    if (nextTo >= children.length) {
      nextTo = children.length;
      nextFrom = nextTo - perPage;
    }

    setIndex({
      from: nextFrom,
      to: nextTo,
    });
  };

  const hasOnlyOnePage = !hasPreviousPage && !hasNextPage;

  useEffect(() => {
    setTimeout(() => {
      setShowDoubleArrow(showDoubleArrow);
    }, 200);
  }, [showDoubleArrow]);

  useEffect(() => {
    if (currentQuestionIndex < index.from || currentQuestionIndex > index.to) {
      moveToCurrentQuestion();
    }
  }, [currentQuestionIndex]);

  const onBlur = () => {
    const current = document.querySelectorAll(`button.overview-icon`);
    if (current && current.focus) {
      current[currentQuestionIndex].tabindex = '0';
    }

    const focused = document.querySelector(
      `button.overview-icon[tabIndex='0']`,
    );
    if (focused && focused.focus) {
      focused.tabindex = '-1';
    }
  };

  return (
    <div className="CharmBracelet">
      <div
        className={cn('question-icon-list', { '--preview': isPreview })}
        ref={questionIconsElement}
        role="tablist"
        onBlur={onBlur}
      >
        {childrenForCurrentPage}
      </div>

      <div
        className="question-icon-list-item --out-of-screen"
        ref={sizerElement}
      >
        {children[0]}
      </div>

      <div className="actions" tabIndex={-1}>
        {doubleArrowVisible && (
          <PaginationArrow
            className={cn({ hidden: hasOnlyOnePage })}
            disabled={!hasPreviousPage}
            onClick={() => moveToPreviousPage()}
            ariaLabel="Skip back to the previous set of questions"
            icon="skip_back_arrow"
          />
        )}
        <PaginationArrow
          onClick={previousClicked}
          disabled={currentQuestionIndex <= 0}
          icon="navigate_back"
          ariaLabel="Go to the previous question"
        />
        {showNavigationNumbers && (
          <div className="item counters">
            {currentQuestionIndex + 1} of {children.length}
          </div>
        )}
        <PaginationArrow
          onClick={nextClicked}
          disabled={currentQuestionIndex >= children.length - 1}
          icon="navigate_next"
          ariaLabel="Go to the next question"
        />
        {doubleArrowVisible && (
          <PaginationArrow
            className={cn({ hidden: hasOnlyOnePage })}
            disabled={!hasNextPage}
            onClick={() => moveToNextPage()}
            ariaLabel="Skip ahead to next set of question"
            icon="skip_forward_arrow"
          />
        )}
      </div>
    </div>
  );
}

const PaginationArrow = ({
  children,
  onClick,
  ariaLabel,
  disabled = false,
  icon,
  className,
}) => {
  return (
    <ButtonWithIcon
      isIcon
      isPrimary
      size="small"
      icon={icon}
      className={className}
      iconClassName="text-white"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || ''}
      tabIndex={disabled ? -1 : 0}
    >
      {children}
    </ButtonWithIcon>
  );
};
